import React, { useState, useEffect } from "react";
import { SiteWide, Images } from "./customComponents";
import Section from "./layouts/Section";
import { notify, money } from '../utility/Helper1';
import { withdrawalActions } from '../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import { allWithdrawalSelect, userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../redux/selectors/index';
import { useNavigate } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import CustomAgo from "./customComponents/CustomAgo";

// MUI Icons
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import HistoryIcon from "@mui/icons-material/History";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

// ── Shared tokens ────────────────────────────────────────────────────────────
const card = {
    background: "#ffffff",
    border: "1px solid rgba(59,130,246,0.08)",
    boxShadow: "0 4px 18px rgba(59,130,246,0.05)",
    borderRadius: 20,
    padding: 20,
};

const label = {
    color: "#64748b",
    fontSize: 11,
    fontFamily: "'DM Sans',sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
};

const STATUS_MAP = {
    0: { label: "Pending", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.35)", color: "#b45309", Icon: HourglassEmptyIcon },
    1: { label: "Processing", bg: "rgba(59,130,246,0.1)", border: "rgba(59,130,246,0.35)", color: "#1d4ed8", Icon: SyncIcon },
    2: { label: "Paid", bg: "rgba(34,197,94,0.1)", border: "rgba(34,197,94,0.35)", color: "#15803d", Icon: CheckCircleOutlineIcon },
    3: { label: "Cancelled", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.35)", color: "#b91c1c", Icon: CancelOutlinedIcon },
};

function StatusBadge({ status }) {
    const s = STATUS_MAP[status] ?? STATUS_MAP[0];
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "4px 12px", borderRadius: 20,
            background: s.bg, border: `1.5px solid ${s.border}`,
            color: s.color, fontSize: 11, fontWeight: 700,
            fontFamily: "'DM Sans',sans-serif",
        }}>
            <s.Icon style={{ fontSize: 13 }} />
            {s.label}
        </span>
    );
}

function WithdrawalHistory() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loadHistoryData = useSelector(allWithdrawalSelect);

    const [loadingText, setLoadingText] = useState('Loading withdrawal history...');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        dispatch(withdrawalActions.onLoadAllWithdrawals("withdrawal/load", response => {
            setLoading(false);
        }));
    }, []);

    const activeCurrency = finance?.[0];
    const filteredHistoryData = loadHistoryData?.filter(
        i => Number(i.currency) === Number(activeCurrency?.type)
    ) || [];

    const totalWithdrawn = filteredHistoryData?.reduce((sum, i) => sum + (parseFloat(i?.readOnlyTotalWithdrawn) || 0), 0) ?? 0;
    const confirmedCount = filteredHistoryData?.filter(i => Number(i.status) === 2).length ?? 0;
    const pendingCount = filteredHistoryData?.filter(i => Number(i.status) < 2).length ?? 0;

    return (
        <SiteWide id="main" title="Withdrawal Records">
            <Section overlayText={loadingText} loading={loading} title="Withdrawal Records">
                <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 32 }}>

                    {/* ── Hero gradient card ──────────────────────────── */}
                    <div style={{
                        borderRadius: 22, padding: "22px 20px",
                        position: "relative", overflow: "hidden",
                        background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 55%,#3b82f6 100%)",
                        boxShadow: "0 10px 28px rgba(37,99,235,0.3)",
                    }}>
                        {/* glow blob */}
                        <div style={{
                            position: "absolute", top: -30, right: -30,
                            width: 160, height: 160, borderRadius: "50%",
                            background: "radial-gradient(circle,rgba(147,197,253,0.22),transparent)",
                            pointerEvents: "none",
                        }} />

                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                            <div>
                                <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                    Total Withdrawn
                                </p>
                                <p style={{ color: "#fff", fontSize: 30, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "6px 0 0", lineHeight: 1.1 }}>
                                    {money(totalWithdrawn, finance?.[0]?.type)}
                                </p>
                                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
                                    <ArrowUpwardIcon style={{ fontSize: 12, color: "#86efac" }} />
                                    <span style={{ color: "#86efac", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                        Withdrawal History
                                    </span>
                                </div>
                            </div>
                            <div style={{
                                width: 48, height: 48, borderRadius: "50%",
                                background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.3)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <AccountBalanceWalletIcon style={{ color: "#fff", fontSize: 24 }} />
                            </div>
                        </div>

                        {/* Stats row */}
                        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                            {[
                                { label: "Total", value: loadHistoryData?.length ?? 0, color: "#fff" },
                                { label: "Confirmed", value: confirmedCount, color: "#86efac" },
                                { label: "Pending", value: pendingCount, color: "#fde68a" },
                            ].map(s => (
                                <div key={s.label} style={{
                                    flex: 1, textAlign: "center",
                                    background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
                                    borderRadius: 12, padding: "8px 6px",
                                }}>
                                    <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: s.color, fontFamily: "'Syne',sans-serif" }}>
                                        {s.value}
                                    </p>
                                    <p style={{ margin: 0, fontSize: 9, color: "rgba(255,255,255,0.65)", fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        {s.label}
                                    </p>
                                </div>
                            ))}
                        </div>

                    </div>

                    {/* ── Transaction list ────────────────────────────── */}
                    {(filteredHistoryData && filteredHistoryData.length > 0) ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {filteredHistoryData.map((item, index) => {
                                const currencyImg = finance?.find(f => f.type == item.currency);
                                const s = STATUS_MAP[item?.status] ?? STATUS_MAP[0];
                                const fee = parseFloat(item?.readOnlyTotalWithdrawn ?? 0) - parseFloat(item?.amount ?? 0);

                                return (
                                    <div key={index} style={{ ...card, padding: 0, overflow: "hidden" }}>
                                        {/* Status accent bar */}
                                        <div style={{ height: 3, background: s.border }} />

                                        <div style={{ padding: "16px 18px" }}>
                                            {/* Row 1: icon + title + status badge */}
                                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                                                {/* Currency avatar */}
                                                <div style={{
                                                    width: 46, height: 46, borderRadius: 14, flexShrink: 0,
                                                    background: "linear-gradient(135deg,#1e3a8a,#2563eb)",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    boxShadow: "0 4px 12px rgba(37,99,235,0.2)",
                                                }}>
                                                    {currencyImg?.image
                                                        ? <Images height={24} source={currencyImg.image} alt="currency" />
                                                        : <AccountBalanceWalletIcon style={{ color: "#fff", fontSize: 20 }} />
                                                    }
                                                </div>

                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a", fontFamily: "'Syne',sans-serif" }}>
                                                        Withdrawal
                                                    </p>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                                                        <ReceiptLongIcon style={{ fontSize: 11, color: "#94a3b8" }} />
                                                        <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }}>
                                                            {item.txn_id}
                                                        </span>
                                                        <CopyToClipboard text={item.txn_id} onCopy={() => notify('Copied!', 1)}>
                                                            <button style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}>
                                                                <ContentCopyIcon style={{ fontSize: 11, color: "#94a3b8" }} />
                                                            </button>
                                                        </CopyToClipboard>
                                                    </div>
                                                </div>

                                                <StatusBadge status={item?.status} />
                                            </div>

                                            {/* Divider */}
                                            <div style={{ height: 1, background: "rgba(59,130,246,0.07)", marginBottom: 14 }} />

                                            {/* Row 2: amounts grid */}
                                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
                                                {[
                                                    { label: "Requested", value: money(item?.readOnlyTotalWithdrawn, item?.currency), color: "#0f172a" },
                                                    { label: "Fee", value: money(fee > 0 ? fee : 0, item?.currency), color: "#ef4444" },
                                                    { label: "Received", value: money(item?.amount, item?.currency), color: "#16a34a" },
                                                ].map(col => (
                                                    <div key={col.label} style={{
                                                        background: "rgba(59,130,246,0.03)",
                                                        border: "1px solid rgba(59,130,246,0.07)",
                                                        borderRadius: 12, padding: "10px 10px",
                                                        textAlign: "center",
                                                    }}>
                                                        <p style={{ margin: 0, ...label, fontSize: 9, marginBottom: 4 }}>{col.label}</p>
                                                        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: col.color, fontFamily: "'Syne',sans-serif" }}>
                                                            {col.value}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Row 3: timestamp */}
                                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                <CalendarTodayIcon style={{ fontSize: 11, color: "#94a3b8" }} />
                                                <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "'DM Sans',sans-serif" }}>
                                                    <CustomAgo time={item?.timestamp} />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* ── Empty state ─────────────────────────────── */
                        <div style={{ ...card, textAlign: "center", padding: "44px 24px" }}>
                            <div style={{
                                width: 72, height: 72, borderRadius: 20, margin: "0 auto 18px",
                                background: "linear-gradient(135deg,rgba(37,99,235,0.08),rgba(59,130,246,0.04))",
                                border: "1.5px solid rgba(59,130,246,0.12)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <AccountBalanceWalletIcon style={{ fontSize: 32, color: "#3b82f6" }} />
                            </div>
                            <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#0f172a", fontFamily: "'Syne',sans-serif" }}>
                                No Withdrawal Records
                            </p>
                            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
                                You haven't made any withdrawal requests yet.
                            </p>
                            <button
                                onClick={() => navigate('/atm')}
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 8,
                                    padding: "13px 28px", borderRadius: 14, border: "none",
                                    background: "linear-gradient(135deg,#1e3a8a,#2563eb)",
                                    color: "#fff", fontSize: 14, fontWeight: 700,
                                    fontFamily: "'Syne',sans-serif", cursor: "pointer",
                                    boxShadow: "0 6px 20px rgba(37,99,235,0.3)",
                                }}
                            >
                                <AddIcon style={{ fontSize: 18 }} />
                                Request Withdrawal
                            </button>
                        </div>
                    )}

                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </Section>
        </SiteWide>
    );
}

export default WithdrawalHistory;